import { EventMap, EventSubscriber, WrappedEvent } from '@drift-labs/sdk';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Env, {
	SubscriberType,
} from '../environmentVariables/EnvironmentVariables';
import useCurrentWalletAdapter from '../hooks/useCurrentWalletAdapter';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import { useDebounce } from 'react-use';
import useUserAccountPubKey from '../hooks/useUserAccountPubKey';
import useCurrentSettings from '../hooks/useCurrentSettings';
import { Subject } from 'rxjs';

const WEBSOCKET_RESUBSCRIBE_TIMEOUT_MS = 2_000;
const WEBSOCKET_ALLOWED_RETRY_ATTEMPTS = 3;

const INITIAL_CONTEXT_VALUE: {
	ready: boolean;
	eventSubscriber: EventSubscriber;
	eventSubject: Subject<WrappedEvent<keyof EventMap>>;
} = { ready: false, eventSubscriber: undefined, eventSubject: undefined };

export const DriftMarketEventSubscriberContext = React.createContext<{
	ready: boolean;
	eventSubscriber?: EventSubscriber;
	eventSubject: Subject<WrappedEvent<keyof EventMap>>;
}>(INITIAL_CONTEXT_VALUE);

const DriftMarketEventSubscriberProvider = (props: PropsWithChildren<any>) => {
	const connection = useDriftStore((s) => s.connection.current);
	const selectedRpc = useDriftStore((s) => s.connection.rpc);

	const driftClient = useDriftStore((s) => s.driftClient.client);
	const currentAuthority = useCurrentWalletAdapter().publicKey;
	const currentUserAccount = useUserAccountPubKey();
	const walletIsConnected = useWalletIsConnected();
	const pollingMultiplier = useDriftStore((s) => s.pollingMultiplier);

	const [contextValue, setContextValue] = useState(INITIAL_CONTEXT_VALUE);

	const eventSubscriberSetting = useCurrentSettings()[0].eventSubscriberType;

	const createEventSubscriber = (eventSubscriberSetting: SubscriberType) => {
		const useWebsocket =
			eventSubscriberSetting === 'websocket' && selectedRpc?.wsValue;

		if (useWebsocket) {
			console.log(`Using websocket eventsubscriber`);
			const eventSubscriber = new EventSubscriber(
				connection,
				driftClient.program,
				{
					maxTx: 128,
					maxEventsPerType: 256,
					orderBy: 'blockchain',
					commitment: 'confirmed',
					logProviderConfig: {
						type: 'websocket',
						resubTimeoutMs: WEBSOCKET_RESUBSCRIBE_TIMEOUT_MS,
						maxReconnectAttempts: WEBSOCKET_ALLOWED_RETRY_ATTEMPTS,
						fallbackFrequency: Env.pollingFrequencyMs * pollingMultiplier, // The websocket provider falls back to polling when it detects issues
					},
				}
			);

			return eventSubscriber;
		} else {
			console.log(`Using polling eventsubscriber`);
			const eventSubscriber = new EventSubscriber(
				connection,
				driftClient.program,
				{
					maxTx: 128,
					maxEventsPerType: 256,
					orderBy: 'blockchain',
					commitment: 'confirmed',
					logProviderConfig: {
						type: 'polling',
						frequency: Env.pollingFrequencyMs * pollingMultiplier,
					},
				}
			);

			return eventSubscriber;
		}
	};

	// The params that subscribing depend on can change in bursts, but we don't want to subscribe+unsubscribe in quick bursts because it's unnecessary. For this reason we're going to make a ticker to sync with these params, and only update it with a debounced hook.
	const [subscribeUpdateTicker, setSubscribeUpdateTicker] = useState([]);

	// The debounced hook which can only update the ticker at a maximum speed
	useDebounce(
		() => {
			setSubscribeUpdateTicker([]);
		},
		1000,
		[
			connection,
			driftClient,
			walletIsConnected && currentAuthority, // To prevent wasteful (un)subscribes , we only actually need to update the subscriber if the combination of these changes
			eventSubscriberSetting,
			pollingMultiplier,
			currentUserAccount,
		]
	);

	useEffect(() => {
		if (!connection) return;
		if (!driftClient) return;
		if (!driftClient.isSubscribed) return;

		const eventSubscriber = createEventSubscriber(eventSubscriberSetting);

		const subject = new Subject<WrappedEvent<keyof EventMap>>();

		eventSubscriber.eventEmitter.on('newEvent', (event) => {
			subject.next(event);
		});

		setContextValue({
			ready: true,
			eventSubscriber,
			eventSubject: subject,
		});

		eventSubscriber.subscribe();

		return () => {
			eventSubscriber.unsubscribe();
			subject.complete();
		};
	}, [subscribeUpdateTicker, eventSubscriberSetting]);

	return (
		<DriftMarketEventSubscriberContext.Provider value={contextValue}>
			{props.children}
		</DriftMarketEventSubscriberContext.Provider>
	);
};

export default DriftMarketEventSubscriberProvider;
